class SuuntoYoutube extends HTMLElement {
  constructor() {
    super();
    this.videoId = this.dataset.videoId || '';
    this.wrap = this.querySelector('[role="youtube-wrap"]');
    this.triggerNode = this.querySelector('[role="youtube-trigger"]');
    this._iframe = null;
    this.autoplay = this.hasAttribute('autoplay');
    this.iframeClass = this.dataset.iframeClass || '';
    this.hiddenNode = this.querySelector('[role="youtube-hidden"]');
    this.immediate = this.hasAttribute('immediate');
    this.preview = this.hasAttribute('preview'); // 新增预览模式
    this.thumbnailSize = this.dataset.thumbnailSize || 'maxres'; // 缩略图尺寸
  }

  connectedCallback() {
    if (this.immediate) {
      // 立即创建 iframe
      this._createIframe();
    } else if (this.preview) {
      // 创建预览图
      this._createPreview();
    } else {
      // 原始的点击触发模式
      this.initEvent();
    }
  }

  /**
   * 创建 YouTube 视频预览图
   */
  _createPreview() {
    if (!this.videoId || !this.wrap) return;

    // 清空容器
    this.wrap.innerHTML = '';

    // 获取缩略图URL
    const thumbnailUrl = this._getThumbnailUrl();

    // 创建预览容器
    const previewContainer = document.createElement('div');
    previewContainer.className = 'youtube-preview-container';
    previewContainer.style.cssText = `
      position: relative;
      width: 100%;
      height: 100%;
      cursor: pointer;
      overflow: hidden;
      border-radius: 12px;
      background: #000;
    `;

    // 创建缩略图
    const thumbnail = document.createElement('img');
    thumbnail.src = thumbnailUrl;
    thumbnail.alt = 'YouTube video thumbnail';
    thumbnail.style.cssText = `
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    `;

    // 创建播放按钮
    const playButton = document.createElement('div');
    playButton.className = 'youtube-play-button play-button'; // 🔥 添加play-button类名
    playButton.innerHTML = `
      <svg width="68" height="48" viewBox="0 0 68 48">
        <path d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z" fill="#f00"></path>
        <path d="M 45,24 27,14 27,34" fill="#fff"></path>
      </svg>
    `;
    playButton.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      transition: transform 0.2s ease;
      filter: drop-shadow(0 2px 8px rgba(0,0,0,0.3));
    `;

    // 添加悬停效果
    previewContainer.addEventListener('mouseenter', () => {
      thumbnail.style.transform = 'scale(1.05)';
      playButton.style.transform = 'translate(-50%, -50%) scale(1.1)';
    });

    previewContainer.addEventListener('mouseleave', () => {
      thumbnail.style.transform = 'scale(1)';
      playButton.style.transform = 'translate(-50%, -50%) scale(1)';
    });

    // 点击事件 - 整个预览容器都可以点击
    previewContainer.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      // 🔥 直接使用自动播放创建iframe
      this._createIframeWithAutoplay();
      // 移除预览容器
      setTimeout(() => {
        if (previewContainer.parentNode) {
          previewContainer.remove();
        }
      }, 100);
    }, { once: true }); // 只能点击一次

    // 组装预览
    previewContainer.appendChild(thumbnail);
    previewContainer.appendChild(playButton);
    this.wrap.appendChild(previewContainer);

    // 处理图片加载错误
    thumbnail.addEventListener('error', () => {
      this._handleThumbnailError(previewContainer);
    });
  }

  /**
   * 获取缩略图URL
   */
  _getThumbnailUrl() {
    const sizeMap = {
      'default': 'default',
      'medium': 'mqdefault',
      'high': 'hqdefault',
      'standard': 'sddefault',
      'maxres': 'maxresdefault'
    };

    const size = sizeMap[this.thumbnailSize] || 'maxresdefault';
    return `https://img.youtube.com/vi/${this.videoId}/${size}.jpg`;
  }

  /**
   * 处理缩略图加载失败
   */
  _handleThumbnailError(container) {
    // 如果maxres失败，尝试使用hq版本
    if (this.thumbnailSize === 'maxres') {
      this.thumbnailSize = 'high';
      const img = container.querySelector('img');
      img.src = this._getThumbnailUrl();
      return;
    }

    // 创建备用预览
    container.innerHTML = `
      <div style="
        display: flex; 
        align-items: center; 
        justify-content: center; 
        height: 100%; 
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        font-size: 18px;
        font-weight: 600;
      ">
        <div style="text-align: center;">
          <div style="font-size: 48px; margin-bottom: 16px;">▶</div>
          <div>Play Video</div>
        </div>
      </div>
    `;
  }

  /**
   * 加载真实视频
   */
  _loadVideo(previewContainer) {
    // 添加加载动画
    previewContainer.style.opacity = '0.7';
    previewContainer.style.pointerEvents = 'none';

    // 创建带自动播放的iframe
    this._createIframeWithAutoplay();

    // 移除预览容器 (延迟移除，等iframe加载完成)
    setTimeout(() => {
      if (previewContainer.parentNode) {
        previewContainer.remove();
      }
    }, 500);
  }

  /**
   * 创建带自动播放的iframe（用于点击播放）
   */
  _createIframeWithAutoplay() {
    if (!this.videoId || this._iframe) return;

    const params = new URLSearchParams({
      enablejsapi: '1',
      modestbranding: '1',
      playsinline: '1',
      rel: this.getAttribute('rel') === '1' ? '1' : '0',
      controls: this.hasAttribute('controls') ? '1' : '0',
      autoplay: '1', // 🔥 强制自动播放
      // mute: '1',      // 🔥 静音以支持自动播放
    });

    const domain = this.hasAttribute('nocookie')
      ? 'https://www.youtube-nocookie.com'
      : 'https://www.youtube.com';

    const src = `${domain}/embed/${this.videoId}?${params.toString()}`;

    const iframe = document.createElement('iframe');
    iframe.setAttribute('src', src);
    iframe.setAttribute(
      'allow',
      'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen'
    );
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('title', this.getAttribute('title') || 'YouTube video player');
    iframe.setAttribute('referrerpolicy', 'no-referrer-when-downgrade');
    iframe.setAttribute('loading', 'lazy');

    iframe.style.opacity = '0';
    iframe.style.transition = 'opacity 0.5s ease-in';

    iframe.addEventListener('load', () => {
      setTimeout(() => {
        iframe.style.opacity = '1';
        this._hideWithFade();
        this.wrap.setAttribute('data-open', '1');
      }, 300);
    });

    iframe.dataset.embedded = '1';
    if (this.iframeClass) iframe.className = this.iframeClass;
    this._iframe = iframe;
    this.wrap.appendChild(iframe);
  }

  // 原有的 _createIframe 方法保持不变...
  _createIframe() {
    if (!this.videoId || this._iframe) return;

    const params = new URLSearchParams({
      enablejsapi: '1',
      modestbranding: '1',
      playsinline: '1',
      rel: this.getAttribute('rel') === '1' ? '1' : '0',
      controls: this.hasAttribute('controls') ? '1' : '0',
      autoplay: this.autoplay ? '1' : '0',
    });

    const domain = this.hasAttribute('nocookie')
      ? 'https://www.youtube-nocookie.com'
      : 'https://www.youtube.com';

    const src = `${domain}/embed/${this.videoId}?${params.toString()}`;

    const iframe = document.createElement('iframe');
    iframe.setAttribute('src', src);
    iframe.setAttribute(
      'allow',
      'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen'
    );
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('title', this.getAttribute('title') || 'YouTube video player');
    iframe.setAttribute('referrerpolicy', 'no-referrer-when-downgrade');
    iframe.setAttribute('loading', 'lazy');

    iframe.style.opacity = '0';
    iframe.style.transition = 'opacity 0.5s ease-in';

    iframe.addEventListener('load', () => {
      setTimeout(() => {
        iframe.style.opacity = '1';
        this._hideWithFade();
        this.wrap.setAttribute('data-open', '1');
      }, 300);
    });

    iframe.dataset.embedded = '1';
    if (this.iframeClass) iframe.className = this.iframeClass;
    this._iframe = iframe;
    this.wrap.appendChild(iframe);
  }

  // 其他方法保持不变...
  _hideWithFade() {
    if (!this.hiddenNode) return;

    this.hiddenNode.style.transition = 'opacity 0.5s ease-out, transform 0.5s ease-out';
    this.hiddenNode.style.opacity = '0';
    this.hiddenNode.style.transform = 'translateY(-10px)';

    setTimeout(() => {
      this.hiddenNode.style.display = 'none';
    }, 500);
  }

  /**
   * 初始化事件监听器
   */
  initEvent() {
    if (!this.wrap || !this.videoId) return;

    const playButton = this.wrap.querySelector('.play-button');
    if (playButton) {
      playButton.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          // 🔥 直接使用自动播放的iframe，实现一键播放
          this._createIframeWithAutoplay();
        },
        { once: true }
      );
    }

    // 如果有trigger节点（向后兼容）
    if (this.triggerNode) {
      this.triggerNode.addEventListener(
        'click',
        () => {
          // 🔥 对于有trigger的模式，也使用自动播放
          this._createIframeWithAutoplay();
        },
        { once: true }
      );
    }
  }
}

if (!customElements.get('suunto-youtube')) {
  customElements.define('suunto-youtube', SuuntoYoutube);
}